import { useAlert } from '@fs/core';
import { memorization } from '@fs/utils';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { UseMutateFunction } from 'react-query';
import { useNavigate } from 'react-router-dom';
import type { TAccessToke, TLoginPayload } from 'src/pages/Login/model';
import type { TResponse } from 'src/types';

export const useAuth = ({
  mutate,
}: {
  mutate?: UseMutateFunction<
    TResponse<TAccessToke>,
    unknown,
    TLoginPayload,
    unknown
  >;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { triggerAlert } = useAlert();
  const login = async (data: TLoginPayload) => {
    mutate &&
      mutate(
        { ...data },
        {
          onSuccess: (res) => {
            localStorage.setItem('access_token', res?.data?.accessToken);
            localStorage.setItem('refresh_token', res?.data?.refreshToken);
            triggerAlert(t('_LOGIN.SUCCESSFUL_LOGIN'), 2000);
            const callback = localStorage.getItem('callback') ?? '/';
            navigate(callback);
          },
        }
      );
  };

  return {
    login,
  };
};

export const logoutLogic = async () => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.replace(`${window.location}/login`);
    window.location.reload();
  } catch (error) {
    console.error('Error during logout logic:', error);
    return null;
  }
};

const refreshToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return logoutLogic();
  }
  try {
    const response = await axios.post<TResponse<TAccessToke>>(
      '/api/v1/panel/auth/refresh',
      { refreshToken }
    );

    const { accessToken, refreshToken: newRefreshToken } = response.data.data;

    if (accessToken && newRefreshToken) {
      window.localStorage.setItem('access_token', accessToken);
      window.localStorage.setItem('refresh_token', newRefreshToken);
      return accessToken;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error during token refresh:', error);
    await logoutLogic();
    return null;
  }
};

const maxAge = 10_000;

export const memoizedRefreshToken = memorization(refreshToken, {
  maxAge,
  cacheKey: (args) =>
    JSON.stringify([args, localStorage.getItem('access_token')]),
});
